import { AgentComposer } from "@/components/agents/AgentComposer";
import { AgentConversation } from "@/components/agents/AgentConversation";
import { AgentGatewayState } from "@/components/agents/AgentGatewayState";
import type { OutgoingMessageAttempt } from "@/components/agents/message-utils";
import {
  agentSessionQueryOptions,
  availableAgentsQueryOptions,
} from "@/components/agents/query-options";
import {
  formatSessionDateTime,
  getSessionStatusPresentation,
} from "@/components/agents/session-list-utils";
import { useAgentApprovalController } from "@/components/agents/use-agent-approval-controller";
import { useAgentEndSessionController } from "@/components/agents/use-agent-end-session-controller";
import { useAgentMessageController } from "@/components/agents/use-agent-message-controller";
import { useAgentRequestInputController } from "@/components/agents/use-agent-request-input-controller";
import { useRouteHeadingFocus } from "@/components/agents/use-route-heading-focus";
import LoadingSpinner from "@/components/common/pages/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { buildMetadata } from "@/lib/metadata";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { type UIEvent, useEffect, useRef } from "react";
import { z } from "zod";
import { findAvailableAgent, requireAgentGatewayAccess } from "../-access";
import {
  getSessionAccessRefetchInterval,
  getSessionErrorState,
} from "../-session-state";

export const Route = createFileRoute("/_main/sales-agent/$agentKey/$sessionId")(
  {
    beforeLoad: ({ context }) => requireAgentGatewayAccess(context.auth),
    component: AgentSessionPage,
    validateSearch: z.object({
      autoStart: z.literal("1").optional(),
    }),
    head: () => ({
      meta: buildMetadata({
        title: "AI Agent Session",
        description: "Review a supplier-owned AI agent session.",
        noIndex: true,
      }),
    }),
  },
);

export function AgentSessionPage() {
  const { agentKey, sessionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const headingRef = useRouteHeadingFocus<HTMLHeadingElement>(
    `${agentKey}:${sessionId}`,
  );
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const shouldFollowConversationRef = useRef(true);
  const automaticStartClaimRef = useRef<string | null>(null);
  const messageController = useAgentMessageController(agentKey, sessionId);
  const startWorkflowRef = useRef(messageController.startWorkflow);
  startWorkflowRef.current = messageController.startWorkflow;
  const approvalController = useAgentApprovalController(agentKey, sessionId);
  const requestInputController = useAgentRequestInputController(
    agentKey,
    sessionId,
  );
  const endSessionController = useAgentEndSessionController(
    agentKey,
    sessionId,
  );
  const agentsQuery = useQuery({
    ...availableAgentsQueryOptions(),
    refetchInterval: getSessionAccessRefetchInterval,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
  });
  const availableAgent = findAvailableAgent(agentsQuery.data, agentKey);
  const sessionQuery = useQuery({
    ...agentSessionQueryOptions(agentKey, sessionId),
    enabled: agentsQuery.isSuccess && Boolean(availableAgent),
    refetchInterval: getSessionAccessRefetchInterval,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
  });
  const eventCount = sessionQuery.data?.events.length ?? 0;
  const outgoingAttemptVersion = messageController.outgoingAttempts
    .map((attempt) => `${attempt.idempotencyKey}:${attempt.status}`)
    .join("|");
  const streamingMessageVersion = messageController.streamingMessages
    .map((message) => `${message.messageKey}:${message.text.length}`)
    .join("|");

  useEffect(() => {
    const session = sessionQuery.data;
    if (search.autoStart !== "1" || !session) {
      return;
    }

    const scope = `${agentKey}:${sessionId}`;
    if (automaticStartClaimRef.current === scope) {
      return;
    }

    // Deferring one task prevents React Strict Mode's development-only effect
    // replay from opening and immediately aborting the real SSE request.
    const timeoutId = globalThis.setTimeout(() => {
      if (automaticStartClaimRef.current === scope) {
        return;
      }
      automaticStartClaimRef.current = scope;
      void navigate({ search: {}, replace: true });
      if (
        session.status.trim().toLowerCase() !== "active" ||
        session.events.length > 0 ||
        messageController.isPending
      ) {
        return;
      }

      shouldFollowConversationRef.current = true;
      startWorkflowRef.current();
    }, 0);

    return () => globalThis.clearTimeout(timeoutId);
  }, [
    agentKey,
    messageController.isPending,
    navigate,
    search.autoStart,
    sessionId,
    sessionQuery.data,
  ]);

  useEffect(() => {
    if (
      (eventCount === 0 &&
        outgoingAttemptVersion.length === 0 &&
        streamingMessageVersion.length === 0) ||
      !shouldFollowConversationRef.current
    ) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      conversationEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [eventCount, outgoingAttemptVersion, streamingMessageVersion]);

  const handleConversationScroll = (event: UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldFollowConversationRef.current = distanceFromBottom < 160;
  };

  const sendMessage = (text: string) => {
    shouldFollowConversationRef.current = true;
    messageController.sendMessage(text);
  };

  const retryMessage = (attempt: OutgoingMessageAttempt) => {
    shouldFollowConversationRef.current = true;
    messageController.retryMessage(attempt);
  };

  if (
    agentsQuery.isPending ||
    (Boolean(availableAgent) && sessionQuery.isPending)
  ) {
    return (
      <div className="flex flex-1 items-center justify-center" aria-busy="true">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  if (agentsQuery.isError) {
    const failure = getSessionErrorState(agentsQuery.error);

    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description={failure.description}
          kind={failure.kind}
          onAction={failure.canRetry ? () => agentsQuery.refetch() : undefined}
          title={failure.title}
        />
      </div>
    );
  }

  if (!availableAgent) {
    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description="This agent is no longer assigned to your supplier."
          kind="forbidden"
          title="Agent access revoked"
        />
      </div>
    );
  }

  if (sessionQuery.isError) {
    const failure = getSessionErrorState(sessionQuery.error);

    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description={failure.description}
          kind={failure.kind}
          onAction={failure.canRetry ? () => sessionQuery.refetch() : undefined}
          title={failure.title}
        />
      </div>
    );
  }

  const session = sessionQuery.data;

  if (!session) {
    return (
      <div className="flex flex-1 items-center p-4 md:p-8">
        <AgentGatewayState
          description="The session did not return a usable response."
          kind="unavailable"
          onAction={() => sessionQuery.refetch()}
          title="Session unavailable"
        />
      </div>
    );
  }

  const status = getSessionStatusPresentation(session.status);
  const sessionActive = session.status.trim().toLowerCase() === "active";
  const answeredInputById = Object.fromEntries(
    session.events.flatMap((event) =>
      event.type === "input_recorded"
        ? [[event.content.interrupt_id, event.content.response]]
        : [],
    ),
  );
  const hasPendingInput = session.events.some(
    (event) =>
      event.type === "input_required" &&
      !(event.content.interrupt_id in answeredInputById),
  );

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-slate-50/40"
      onScroll={handleConversationScroll}
    >
      <header className="min-w-0 border-slate-200 border-b bg-white px-4 py-4 md:px-8 md:py-5">
        <div className="mx-auto w-full min-w-0 max-w-7xl">
          <Link
            className="inline-flex items-center gap-1 text-slate-500 text-sm hover:text-slate-900"
            search={{ agent: agentKey }}
            to="/sales-agent"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
            Back to sessions
          </Link>
          <div className="mt-3 flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <h1
                  className="min-w-0 rounded-sm font-semibold text-2xl text-slate-900 outline-none [overflow-wrap:anywhere] focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  ref={headingRef}
                  tabIndex={-1}
                >
                  {session.title || "Untitled session"}
                </h1>
                <Badge className="rounded-full" variant="secondary">
                  {status.label}
                </Badge>
              </div>
              <p className="mt-1 break-all text-slate-500 text-sm">
                {availableAgent.display_name} · {session.id}
              </p>
            </div>

            {sessionActive ? (
              <Button
                className="h-[34px] w-fit self-start rounded-lg bg-[#FF6767] px-[14px] py-2 text-sm text-white shadow-none hover:bg-[#FF6767]/90 md:h-[45px] md:self-center md:px-[18px] md:py-3 md:text-base"
                disabled={endSessionController.isPending}
                onClick={() => endSessionController.setDialogOpen(true)}
                type="button"
              >
                {endSessionController.isPending ? "Stopping…" : "Stop Session"}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto grid w-full min-w-0 max-w-7xl flex-1 gap-6 p-4 pb-[calc(11rem+env(safe-area-inset-bottom))] md:p-8 md:pb-8 xl:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0">
          <AgentConversation
            answeredInputById={answeredInputById}
            approvalErrorById={approvalController.errorByApprovalId}
            canApproveOutboundActions={
              sessionActive &&
              availableAgent.capabilities.approve_outbound_actions
            }
            canApproveVerifiedLeads={
              sessionActive &&
              availableAgent.capabilities.approve_verified_leads
            }
            events={session.events}
            inputErrorById={requestInputController.errorByInputId}
            onApprovalDecision={approvalController.decideApproval}
            onRetryApprovalContinuation={
              approvalController.retryApprovalContinuation
            }
            onRequestInputResponse={requestInputController.respondToInput}
            onRetry={retryMessage}
            outgoingAttempts={messageController.outgoingAttempts}
            pendingApprovalId={approvalController.pendingApprovalId}
            pendingContinuationId={approvalController.pendingContinuationId}
            pendingInputId={requestInputController.pendingInputId}
            retryDisabled={!sessionActive || messageController.isPending}
            sessionActive={sessionActive}
            streamingMessages={messageController.streamingMessages}
          />
          <AgentComposer
            announcement={messageController.announcement}
            disabled={!sessionActive || hasPendingInput}
            disabledReason={
              hasPendingInput
                ? "Answer the agent's pending question above before sending another message."
                : "This session has ended. Its conversation and approval history remain available for auditing."
            }
            errorMessage={messageController.composerError}
            onChange={messageController.setDraft}
            onSubmit={sendMessage}
            status={messageController.composerStatus}
            value={messageController.draft}
          />
          <div aria-hidden="true" ref={conversationEndRef} />
        </div>

        <aside
          aria-labelledby="session-details-heading"
          className="h-fit rounded-xl border border-slate-200 bg-white p-5 shadow-xs"
        >
          <h2 className="font-semibold" id="session-details-heading">
            Session details
          </h2>
          <dl className="mt-4 grid min-w-0 gap-4 text-sm sm:grid-cols-2 xl:grid-cols-1">
            <div className="min-w-0">
              <dt className="text-slate-500">Created by</dt>
              <dd className="mt-1 font-medium text-slate-900 [overflow-wrap:anywhere]">
                {session.created_by.name ?? "Supplier administrator"}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-slate-500">Normalized events</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {session.events.length}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-slate-500">Created</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {formatSessionDateTime(session.created_at)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-slate-500">Last activity</dt>
              <dd className="mt-1 font-medium text-slate-900">
                {formatSessionDateTime(session.updated_at)}
              </dd>
            </div>
          </dl>
        </aside>
      </main>

      <Dialog
        onOpenChange={(open) => {
          if (!endSessionController.isPending) {
            endSessionController.setDialogOpen(open);
          }
        }}
        open={endSessionController.dialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stop this session?</DialogTitle>
            <DialogDescription>
              The agents will stop accepting new messages and approval
              decisions. The complete conversation and audit history will remain
              available.
            </DialogDescription>
          </DialogHeader>

          {endSessionController.errorMessage ? (
            <p className="text-destructive text-sm" role="alert">
              {endSessionController.errorMessage}
            </p>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button
                disabled={endSessionController.isPending}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              className="bg-[#FF6767] text-white hover:bg-[#FF6767]/90"
              disabled={endSessionController.isPending}
              onClick={endSessionController.endSession}
              type="button"
            >
              {endSessionController.isPending
                ? "Stopping session…"
                : "Stop Session"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
